import React from 'react';
import { DeckSlideWrapper } from '../DeckSlideWrapper';
import { GitBranch, Target, CheckCircle2 } from 'lucide-react';

export const Slide08TractionGTM: React.FC = () => {
  return (
    <DeckSlideWrapper
      slideNumber={8}
      totalSlides={12}
      category="Traction &amp; GTM"
      title="Go-To-Market &amp; Developer-First Adoption"
      subtitle="Combining frictionless open-core developer distribution with top-down enterprise CISO sales."
    >
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-3.5 sm:gap-5 items-stretch">
        {/* Left GTM Funnel */}
        <div className="lg:col-span-7 flex flex-col justify-between p-3.5 sm:p-4.5 rounded-2xl bg-white/90 dark:bg-navy-850/80 border border-slate-200 dark:border-navy-700 shadow-sm">
          <div className="flex items-center gap-2 mb-2.5 sm:mb-3">
            <GitBranch className="w-4 h-4 text-gold-500" />
            <h4 className="font-mono text-xs font-bold text-slate-900 dark:text-white uppercase tracking-wider">
              The 3-Stage Land &amp; Expand Funnel
            </h4>
          </div>

          <div className="space-y-2 sm:space-y-2.5">
            {/* Stage 1 */}
            <div className="p-2.5 sm:p-3 rounded-xl bg-slate-100/90 dark:bg-navy-900/80 border border-slate-200 dark:border-navy-800">
              <div className="flex items-center justify-between mb-0.5 sm:mb-1">
                <span className="font-mono text-[11px] sm:text-xs font-bold text-amber-700 dark:text-gold-400">
                  STAGE 01 // BOTTOM-UP DEVELOPERS
                </span>
                <span className="text-[10px] font-mono text-slate-500">Free / Open Core</span>
              </div>
              <p className="text-[11px] sm:text-xs text-slate-600 dark:text-slate-300 leading-relaxed">
                Engineers run <code>docker-compose up</code> or use Cloudflare edge proxy for personal/team projects to instantly test multiple LLMs with zero API key leakage.
              </p>
            </div>

            {/* Stage 2 */}
            <div className="p-2.5 sm:p-3 rounded-xl bg-slate-100/90 dark:bg-navy-900/80 border border-slate-200 dark:border-navy-800">
              <div className="flex items-center justify-between mb-0.5 sm:mb-1">
                <span className="font-mono text-[11px] sm:text-xs font-bold text-blue-600 dark:text-blue-400">
                  STAGE 02 // TEAM GOVERNANCE &amp; SAAS
                </span>
                <span className="text-[10px] font-mono text-slate-500">$99 – $499/mo</span>
              </div>
              <p className="text-[11px] sm:text-xs text-slate-600 dark:text-slate-300 leading-relaxed">
                Team leads connect credit cards via LemonSqueezy to set budget guardrails, monitor spend analytics, and share centralized keys across 10–50 developers.
              </p>
            </div>

            {/* Stage 3 */}
            <div className="p-2.5 sm:p-3 rounded-xl bg-slate-100/90 dark:bg-navy-900/80 border border-slate-200 dark:border-navy-800">
              <div className="flex items-center justify-between mb-0.5 sm:mb-1">
                <span className="font-mono text-[11px] sm:text-xs font-bold text-emerald-600 dark:text-emerald-400">
                  STAGE 03 // CISO ENTERPRISE MANDATE
                </span>
                <span className="text-[10px] font-mono text-slate-500">$36K – $120K ARR</span>
              </div>
              <p className="text-[11px] sm:text-xs text-slate-600 dark:text-slate-300 leading-relaxed">
                CISO and VP Compliance mandate Workouse across the entire enterprise for SAML 2.0 SSO, strict in-memory DLP sanitization, and immutable audit logs.
              </p>
            </div>
          </div>
        </div>

        {/* Right Current Signals & Target Verticals */}
        <div className="lg:col-span-5 flex flex-col justify-between p-3.5 sm:p-4.5 rounded-2xl bg-slate-900 dark:bg-navy-950 text-slate-200 border border-slate-700 dark:border-navy-700 shadow-surface space-y-2.5 sm:space-y-3">
          <div className="flex items-center gap-2 border-b border-slate-800 dark:border-navy-800 pb-2">
            <Target className="w-4 h-4 text-gold-400" />
            <h4 className="font-mono text-xs font-bold text-white uppercase tracking-wider">
              Target Verticals &amp; Traction
            </h4>
          </div>

          <div className="space-y-1.5 sm:space-y-2 text-[11px] sm:text-xs">
            <div className="flex items-center gap-2">
              <CheckCircle2 className="w-4 h-4 text-emerald-400 shrink-0" />
              <span><strong>Fintech &amp; Banking:</strong> Strict PCI-DSS &amp; GLBA prompt privacy.</span>
            </div>
            <div className="flex items-center gap-2">
              <CheckCircle2 className="w-4 h-4 text-emerald-400 shrink-0" />
              <span><strong>Healthcare &amp; MedTech:</strong> Zero raw PHI/HIPAA leakage to LLMs.</span>
            </div>
            <div className="flex items-center gap-2">
              <CheckCircle2 className="w-4 h-4 text-emerald-400 shrink-0" />
              <span><strong>LegalTech &amp; Advisory:</strong> Ephemeral client privilege protection.</span>
            </div>
            <div className="flex items-center gap-2">
              <CheckCircle2 className="w-4 h-4 text-emerald-400 shrink-0" />
              <span><strong>High-Growth B2B SaaS:</strong> Multi-tenant LLM cost containment.</span>
            </div>
          </div>

          <div className="p-2.5 sm:p-3 rounded-xl bg-slate-950 dark:bg-navy-900 border border-slate-800 dark:border-navy-800 space-y-0.5 sm:space-y-1 font-mono text-[10px] sm:text-[11px]">
            <div className="text-gold-400 font-bold">CURRENT STATUS:</div>
            <div className="text-slate-300">• Authoritative Architecture &amp; Gateway Core complete.</div>
            <div className="text-slate-300">• Live multi-lingual Landing &amp; Waitlist infrastructure.</div>
            <div className="text-slate-300">• Design partner pipeline active for Q3 2026 Beta.</div>
          </div>
        </div>
      </div>
    </DeckSlideWrapper>
  );
};
